// SCR-STAFF-703 (AP-INT-05؛ US-MED-051، AC-MED-051.1، BR-MED-051): صف «نتایج نامعلوم» — پیام‌های ORU/FHIR که با شماره
// درخواست یا (کد ملی + تاریخ + آزمایه) تطبیق قطعی نداشتند. هیچ پیامی خودکار به پرونده نمی‌چسبد؛ کاربر دارای
// med.result.enter مراجعه‌کننده (و در صورت وجود، درخواست) را انتخاب می‌کند یا پیام بی‌ربط را با دلیل حذف می‌کند.
// کد ملی اعلامی در پاسخ سرور ماسک است و مقادیر داده Clinical‌اند، پس صفحه چیزی را جای دیگری ذخیره نمی‌کند.
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {
  unwrap,
  type MedInboundLabMessageView,
  type MedInboundLabStatus,
  type MedOrderView,
  type PatientSearchItem,
} from '@phoenix/api-client';
import { useAccess, useIdempotencyKey, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  DataTable,
  faDigits,
  FormDialog,
  ListPage,
  ReasonDialog,
  toJalali,
  toJalaliDateTime,
  useDisplaySettings,
  type DataTableColumn,
} from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { FlagChip } from './common';
import { InboundStatusChip, labExchangeKeys, SCR_STAFF_703 } from './labExchange';
import { PatientPicker } from '../patients/PatientPicker';

type Filter = MedInboundLabStatus | 'all';

export function UnmatchedResultsPage() {
  const { t } = useTranslation('med');
  const { tenantApi, session } = useTenantSession();
  const { timeZone } = useDisplaySettings();
  const access = useAccess();
  const queryClient = useQueryClient();
  const tenantId = session?.tenantId;
  const [filter, setFilter] = useState<Filter>('Unmatched');
  const [expanded, setExpanded] = useState<string | null>(null);
  const [matching, setMatching] = useState<MedInboundLabMessageView | null>(null);
  const [discarding, setDiscarding] = useState<MedInboundLabMessageView | null>(null);
  const canResolve = access.can('med.result.enter');

  const rows = useQuery({
    queryKey: labExchangeKeys.inbound(tenantId, filter),
    queryFn: () =>
      unwrap(
        tenantApi.GET('/api/v1/medical-orders/inbound-lab-messages', {
          params: { query: filter === 'all' ? {} : { status: filter } },
        }),
      ),
  });

  const discard = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      unwrap(
        tenantApi.POST('/api/v1/medical-orders/inbound-lab-messages/{id}:discard', {
          params: { path: { id } },
          body: { reason },
        }),
      ),
    onSuccess: () => {
      setDiscarding(null);
      void queryClient.invalidateQueries({ queryKey: labExchangeKeys.inbound(tenantId, filter) });
    },
  });

  const columns: DataTableColumn<MedInboundLabMessageView>[] = [
    { id: 'status', headerKey: 'med:inbound_lab.status', cell: (row) => <InboundStatusChip status={row.status} /> },
    {
      id: 'sender',
      headerKey: 'med:inbound_lab.sender',
      cell: (row) => (
        <Stack spacing={0.25}>
          <span data-testid={`med-inbound-sender-${row.id}`}>{row.sendingFacility ?? t('med:inbound_lab.unknown_sender')}</span>
          <Typography variant="caption" color="text.secondary">
            {t(`med:inbound_lab_transport.${row.transport}`, { defaultValue: row.transport })}
          </Typography>
        </Stack>
      ),
    },
    {
      id: 'patient',
      headerKey: 'med:common.patient',
      cell: (row) => (
        <Stack spacing={0.25}>
          <span data-testid={`med-inbound-patient-${row.id}`}>{row.patientName ?? t('med:inbound_lab.no_patient_name')}</span>
          {row.patientNationalId ? (
            <Typography variant="caption" color="text.secondary" data-testid={`med-inbound-national-${row.id}`}>
              <bdi dir="ltr">{faDigits(row.patientNationalId)}</bdi>
            </Typography>
          ) : null}
        </Stack>
      ),
    },
    {
      id: 'order',
      headerKey: 'med:inbound_lab.placer_order',
      cell: (row) => (
        <bdi dir="ltr" data-testid={`med-inbound-order-${row.id}`}>
          {row.placerOrderNumber ?? row.accessionNumber ?? '—'}
        </bdi>
      ),
    },
    {
      id: 'sample',
      headerKey: 'med:inbound_lab.sample_date',
      cell: (row) => (row.sampleDate ? toJalali(row.sampleDate) : '—'),
    },
    {
      id: 'reason',
      headerKey: 'med:inbound_lab.reason',
      cell: (row) => (
        <span data-testid={`med-inbound-reason-${row.id}`}>
          {row.reason ? t(`med:inbound_lab_reason.${row.reason}`, { defaultValue: row.reason }) : '—'}
        </span>
      ),
    },
    {
      id: 'received',
      headerKey: 'med:inbound_lab.received_at',
      cell: (row) => toJalaliDateTime(row.receivedAt, { timeZone }),
    },
    {
      id: 'actions',
      headerKey: 'med:common.actions',
      cell: (row) => (
        <Stack direction="row" spacing={0.5}>
          <Button size="small" onClick={() => setExpanded(expanded === row.id ? null : row.id)} data-testid={`med-inbound-values-${row.id}`}>
            {t(expanded === row.id ? 'med:inbound_lab.hide_values' : 'med:inbound_lab.show_values', { count: row.valueCount })}
          </Button>
          {canResolve && row.status === 'Unmatched' ? (
            <>
              <Button size="small" variant="outlined" onClick={() => setMatching(row)} data-testid={`med-inbound-match-${row.id}`}>
                {t('med:inbound_lab.match')}
              </Button>
              <Button size="small" color="error" onClick={() => setDiscarding(row)} data-testid={`med-inbound-discard-${row.id}`}>
                {t('med:inbound_lab.discard')}
              </Button>
            </>
          ) : null}
        </Stack>
      ),
    },
  ];

  const current = (rows.data?.items ?? []).find((row) => row.id === expanded) ?? null;

  return (
    <ListPage
      screenId={SCR_STAFF_703}
      titleKey="med:inbound_lab.title"
      subtitleKey="med:inbound_lab.subtitle"
      filters={
        <TextField
          select
          size="small"
          label={t('med:inbound_lab.status')}
          value={filter}
          onChange={(event) => setFilter(event.target.value as Filter)}
          data-testid="med-inbound-filter"
          sx={{ minWidth: 220 }}
        >
          <MenuItem value="Unmatched">{t('med:inbound_lab_status.Unmatched')}</MenuItem>
          <MenuItem value="Matched">{t('med:inbound_lab_status.Matched')}</MenuItem>
          <MenuItem value="Discarded">{t('med:inbound_lab_status.Discarded')}</MenuItem>
          <MenuItem value="all">{t('med:inbound_lab.filter_all')}</MenuItem>
        </TextField>
      }
    >
      <Stack spacing={2}>
        <Alert severity="info" data-testid="med-inbound-hint">
          {t('med:inbound_lab.never_auto_attached')}
        </Alert>
        <DataTable
          rows={rows.data?.items}
          columns={columns}
          getRowId={(row) => row.id}
          isLoading={rows.isLoading}
          error={rows.error}
          onRetry={() => void rows.refetch()}
          emptyTitleKey="med:inbound_lab.empty"
          captionKey="med:inbound_lab.title"
        />
        <Collapse in={Boolean(current)} unmountOnExit>
          {current ? <InboundValues message={current} /> : null}
        </Collapse>
      </Stack>
      {matching ? (
        <MatchDialog
          message={matching}
          onClose={() => setMatching(null)}
          onMatched={() => {
            setMatching(null);
            void queryClient.invalidateQueries({ queryKey: labExchangeKeys.inbound(tenantId, filter) });
          }}
        />
      ) : null}
      <ReasonDialog
        open={Boolean(discarding)}
        titleKey="med:inbound_lab.discard_title"
        messageKey="med:inbound_lab.discard_message"
        confirmKey="med:inbound_lab.discard"
        busy={discard.isPending}
        error={discard.error}
        onCancel={() => setDiscarding(null)}
        onConfirm={(reason) => discarding && discard.mutate({ id: discarding.id, reason })}
      />
    </ListPage>
  );
}

/** مقادیر پیام (داده Clinical): نام، مقدار، واحد، محدوده مرجع و پرچم؛ گزارش متنی در پایان. */
function InboundValues({ message }: { message: MedInboundLabMessageView }) {
  const { t } = useTranslation('med');
  return (
    <Stack spacing={1} data-testid={`med-inbound-values-panel-${message.id}`}>
      <Typography variant="subtitle2">{message.reportTitle ?? t('med:inbound_lab.values')}</Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{t('med:result.analyte')}</TableCell>
            <TableCell>{t('med:result.value')}</TableCell>
            <TableCell>{t('med:result.unit')}</TableCell>
            <TableCell>{t('med:result.reference')}</TableCell>
            <TableCell>{t('med:result.flag')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {message.values.map((value) => (
            <TableRow key={value.code} data-testid={`med-inbound-value-${message.id}-${value.code}`}>
              <TableCell>{value.name ?? value.code}</TableCell>
              <TableCell>
                <bdi dir="ltr">{faDigits(value.value ?? '—')}</bdi>
              </TableCell>
              <TableCell>{value.unit ?? '—'}</TableCell>
              <TableCell>
                <bdi dir="ltr">{faDigits(value.referenceRange ?? '—')}</bdi>
              </TableCell>
              <TableCell>
                <FlagChip flag={value.flag} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {message.reportText ? <Typography variant="body2">{message.reportText}</Typography> : null}
    </Stack>
  );
}

/** تطبیق دستی: مراجعه‌کننده الزامی و درخواست باز همان مراجعه‌کننده اختیاری (AC-MED-051.1). */
function MatchDialog({
  message,
  onClose,
  onMatched,
}: {
  message: MedInboundLabMessageView;
  onClose: () => void;
  onMatched: () => void;
}) {
  const { t } = useTranslation('med');
  const { tenantApi } = useTenantSession();
  const idempotency = useIdempotencyKey();
  const [patient, setPatient] = useState<PatientSearchItem | null>(null);
  const [orderId, setOrderId] = useState('');

  const orders = useQuery({
    queryKey: ['medorders', 'patient-orders', patient?.id ?? 'none'],
    enabled: Boolean(patient),
    queryFn: () =>
      unwrap(
        tenantApi.GET('/api/v1/medical-orders/patients/{patientId}/orders', {
          params: { path: { patientId: patient!.id } },
        }),
      ),
  });

  const match = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/medical-orders/inbound-lab-messages/{id}:match', {
          params: { path: { id: message.id } },
          headers: { 'Idempotency-Key': idempotency.current() },
          body: { patientId: patient?.id ?? '', orderId: orderId || null },
        }),
      ),
    onSettled: (_data, error) => idempotency.settle(error),
    onSuccess: onMatched,
  });

  const open = (orders.data?.items ?? []).filter((order: MedOrderView) => order.status !== 'Cancelled');

  return (
    <FormDialog
      open
      titleKey="med:inbound_lab.match_title"
      screenId={SCR_STAFF_703}
      onSubmit={() => patient && match.mutate()}
      onClose={onClose}
      isDirty={Boolean(patient)}
      isSubmitting={match.isPending}
      submitError={match.error}
      submitKey="med:inbound_lab.match"
    >
      <Stack spacing={2}>
        <Alert severity="info">
          {t('med:inbound_lab.match_hint', {
            sender: message.sendingFacility ?? t('med:inbound_lab.unknown_sender'),
            name: message.patientName ?? t('med:inbound_lab.no_patient_name'),
          })}
        </Alert>
        <PatientPicker labelKey="med:inbound_lab.patient" value={patient} onChange={setPatient} required testId="med-inbound-patient-picker" />
        <TextField
          select
          fullWidth
          label={t('med:inbound_lab.order')}
          value={orderId}
          onChange={(event) => setOrderId(event.target.value)}
          disabled={!patient || orders.isLoading}
          slotProps={{ htmlInput: { 'data-testid': 'med-inbound-order-select' } }}
        >
          <MenuItem value="">{t('med:inbound_lab.no_order')}</MenuItem>
          {open.map((order: MedOrderView) => (
            <MenuItem key={order.id} value={order.id}>
              {order.number} — {order.items.map((item) => item.name).join(t('med:common.list_separator'))}
            </MenuItem>
          ))}
        </TextField>
      </Stack>
    </FormDialog>
  );
}
